/**
 * RenewalCard Component
 *
 * Displays a renewal summary card with status indicators and actions.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';

/**
 * Renewal status configurations
 */
const STATUS_CONFIG = {
  upcoming: {
    label: 'renewal.status.upcoming',
    color: 'var(--info)',
    bgColor: 'var(--info-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>`,
  },
  in_review: {
    label: 'renewal.status.in_review',
    color: 'var(--warning)',
    bgColor: 'var(--warning-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>`,
  },
  action_taken: {
    label: 'renewal.status.action_taken',
    color: 'var(--success)',
    bgColor: 'var(--success-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="20 6 9 17 4 12"/>
    </svg>`,
  },
  auto_renewed: {
    label: 'renewal.status.auto_renewed',
    color: 'var(--secondary)',
    bgColor: 'var(--secondary-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
    </svg>`,
  },
  terminated: {
    label: 'renewal.status.terminated',
    color: 'var(--error)',
    bgColor: 'var(--error-light)',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>`,
  },
};

/**
 * Renewal action configurations
 */
const ACTION_CONFIG = {
  renew: { label: 'renewal.action.renew', color: 'var(--success)' },
  renegotiate: { label: 'renewal.action.renegotiate', color: 'var(--warning)' },
  terminate: { label: 'renewal.action.terminate', color: 'var(--error)' },
  undecided: { label: 'renewal.action.undecided', color: 'var(--text-secondary)' },
};

/**
 * Priority configurations
 */
const PRIORITY_CONFIG = {
  critical: { label: 'renewal.priority.critical', color: '#dc2626', bgColor: '#fef2f2' },
  high: { label: 'renewal.priority.high', color: '#ea580c', bgColor: '#fff7ed' },
  medium: { label: 'renewal.priority.medium', color: '#ca8a04', bgColor: '#fefce8' },
  low: { label: 'renewal.priority.low', color: '#65a30d', bgColor: '#f7fee7' },
};

/**
 * Format currency (uses user's preferred currency as default)
 */
function formatCurrency(amount, currency = null) {
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  const currencyCode = currency || getCurrency();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const lang = getCurrentLanguage();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(dateStr).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get urgency class based on days until renewal
 */
function getUrgencyClass(daysUntil) {
  if (daysUntil < 0) return 'overdue';
  if (daysUntil < 14) return 'critical';
  if (daysUntil < 30) return 'urgent';
  if (daysUntil < 60) return 'attention';
  return 'routine';
}

/**
 * Get urgency label
 */
function getUrgencyLabel(daysUntil) {
  if (daysUntil < 0) return t('renewal.urgency.overdue');
  if (daysUntil < 14) return t('renewal.urgency.critical');
  if (daysUntil < 30) return t('renewal.urgency.urgent');
  if (daysUntil < 60) return t('renewal.urgency.attention');
  return t('renewal.urgency.routine');
}

/**
 * Create a renewal card element
 *
 * @param {Object} renewal - Renewal data
 * @param {Object} options - Card options
 * @returns {HTMLElement} The card element
 */
export function createRenewalCard(renewal, options = {}) {
  const {
    onView = null,
    onAction = null,
    compact = false,
  } = options;

  const statusConfig = STATUS_CONFIG[renewal.status] || STATUS_CONFIG.upcoming;
  const actionConfig = ACTION_CONFIG[renewal.action] || ACTION_CONFIG.undecided;
  const priorityConfig = PRIORITY_CONFIG[renewal.priority] || PRIORITY_CONFIG.medium;
  const urgencyClass = getUrgencyClass(renewal.daysUntilRenewal);
  const urgencyLabel = getUrgencyLabel(renewal.daysUntilRenewal);

  const card = document.createElement('div');
  card.className = `renewal-card ${compact ? 'renewal-card--compact' : ''} urgency-${urgencyClass}`;
  card.setAttribute('data-renewal-id', renewal.id);

  card.innerHTML = `
    <div class="renewal-card__header">
      <div class="renewal-card__title-section">
        <h3 class="renewal-card__title">${renewal.contractTitle || renewal.contractNumber}</h3>
        <span class="renewal-card__vendor">${renewal.vendorName}</span>
      </div>
      <div class="renewal-card__badges">
        <span class="renewal-card__priority" style="background: ${priorityConfig.bgColor}; color: ${priorityConfig.color}">
          ${t(priorityConfig.label)}
        </span>
        <span class="renewal-card__status" style="background: ${statusConfig.bgColor}; color: ${statusConfig.color}">
          ${statusConfig.icon}
          ${t(statusConfig.label)}
        </span>
      </div>
    </div>

    <div class="renewal-card__body">
      <div class="renewal-card__info-grid">
        <div class="renewal-card__info-item">
          <span class="renewal-card__info-label">${t('renewal.renewalDate')}</span>
          <span class="renewal-card__info-value">${formatDate(renewal.renewalDate)}</span>
        </div>
        <div class="renewal-card__info-item">
          <span class="renewal-card__info-label">${t('renewal.daysLeft')}</span>
          <span class="renewal-card__info-value urgency-text-${urgencyClass}">
            ${renewal.daysUntilRenewal} ${t('renewal.days')}
            <small class="urgency-badge urgency-badge--${urgencyClass}">${urgencyLabel}</small>
          </span>
        </div>
        <div class="renewal-card__info-item">
          <span class="renewal-card__info-label">${t('renewal.contractValue')}</span>
          <span class="renewal-card__info-value renewal-card__value">
            ${formatCurrency(renewal.currentValue, renewal.currency)}
          </span>
        </div>
        <div class="renewal-card__info-item">
          <span class="renewal-card__info-label">${t('renewal.plannedAction')}</span>
          <span class="renewal-card__info-value" style="color: ${actionConfig.color}">
            ${t(actionConfig.label)}
          </span>
        </div>
      </div>

      ${renewal.optOutDeadline ? `
        <div class="renewal-card__opt-out">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          ${t('renewal.optOutDeadline')}: ${formatDate(renewal.optOutDeadline)}
          ${renewal.daysUntilOptOut !== null ? `(${renewal.daysUntilOptOut} ${t('renewal.days')})` : ''}
        </div>
      ` : ''}

      ${renewal.assigneeName ? `
        <div class="renewal-card__assignee">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          ${t('renewal.assignedTo')}: ${renewal.assigneeName}
        </div>
      ` : ''}
    </div>

    <div class="renewal-card__footer">
      ${renewal.notesCount > 0 ? `
        <span class="renewal-card__notes-count">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          ${renewal.notesCount} ${t('renewal.notes')}
        </span>
      ` : ''}
      <div class="renewal-card__actions">
        <button class="btn btn--secondary btn--sm renewal-card__view-btn">
          ${t('renewal.viewDetails')}
        </button>
        ${renewal.action === 'undecided' ? `
          <button class="btn btn--primary btn--sm renewal-card__action-btn">
            ${t('renewal.takeAction')}
          </button>
        ` : ''}
      </div>
    </div>
  `;

  // Event listeners
  const viewBtn = card.querySelector('.renewal-card__view-btn');
  if (viewBtn && onView) {
    viewBtn.addEventListener('click', () => onView(renewal));
  }

  const actionBtn = card.querySelector('.renewal-card__action-btn');
  if (actionBtn && onAction) {
    actionBtn.addEventListener('click', () => onAction(renewal));
  }

  // Make card clickable
  if (onView) {
    card.style.cursor = 'pointer';
    card.addEventListener('click', (e) => {
      if (!e.target.closest('button')) {
        onView(renewal);
      }
    });
  }

  return card;
}

/**
 * Create styles for renewal cards
 */
export function injectRenewalCardStyles() {
  if (document.getElementById('renewal-card-styles')) return;

  const styles = document.createElement('style');
  styles.id = 'renewal-card-styles';
  styles.textContent = `
    .renewal-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      padding: var(--space-4);
      transition: box-shadow 0.2s, border-color 0.2s;
    }

    .renewal-card:hover {
      box-shadow: var(--shadow-md);
      border-color: var(--primary);
    }

    .renewal-card.urgency-critical {
      border-left: 4px solid #dc2626;
    }

    .renewal-card.urgency-urgent {
      border-left: 4px solid #ea580c;
    }

    .renewal-card.urgency-attention {
      border-left: 4px solid #ca8a04;
    }

    .renewal-card.urgency-routine {
      border-left: 4px solid #65a30d;
    }

    .renewal-card.urgency-overdue {
      border-left: 4px solid #dc2626;
      background: #fef2f2;
    }

    .renewal-card__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: var(--space-3);
      margin-bottom: var(--space-3);
    }

    .renewal-card__title-section {
      flex: 1;
      min-width: 0;
    }

    .renewal-card__title {
      font-size: var(--text-lg);
      font-weight: 600;
      color: var(--text-primary);
      margin: 0 0 var(--space-1);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .renewal-card__vendor {
      font-size: var(--text-sm);
      color: var(--text-secondary);
    }

    .renewal-card__badges {
      display: flex;
      gap: var(--space-2);
      flex-shrink: 0;
    }

    .renewal-card__priority,
    .renewal-card__status {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      padding: var(--space-1) var(--space-2);
      border-radius: var(--radius-full);
      font-size: var(--text-xs);
      font-weight: 500;
    }

    .renewal-card__status svg {
      width: 14px;
      height: 14px;
    }

    .renewal-card__body {
      margin-bottom: var(--space-3);
    }

    .renewal-card__info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--space-3);
      margin-bottom: var(--space-3);
    }

    .renewal-card__info-item {
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
    }

    .renewal-card__info-label {
      font-size: var(--text-xs);
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .renewal-card__info-value {
      font-size: var(--text-sm);
      color: var(--text-primary);
      font-weight: 500;
    }

    .renewal-card__value {
      font-size: var(--text-base);
      font-weight: 600;
    }

    .urgency-text-critical { color: #dc2626; }
    .urgency-text-urgent { color: #ea580c; }
    .urgency-text-attention { color: #ca8a04; }
    .urgency-text-routine { color: #65a30d; }
    .urgency-text-overdue { color: #dc2626; }

    .urgency-badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: var(--radius-sm);
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      margin-left: var(--space-1);
    }

    .urgency-badge--critical { background: #fef2f2; color: #dc2626; }
    .urgency-badge--urgent { background: #fff7ed; color: #ea580c; }
    .urgency-badge--attention { background: #fefce8; color: #ca8a04; }
    .urgency-badge--routine { background: #f7fee7; color: #65a30d; }
    .urgency-badge--overdue { background: #dc2626; color: white; }

    .renewal-card__opt-out {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-2) var(--space-3);
      background: #fff7ed;
      border-radius: var(--radius);
      font-size: var(--text-sm);
      color: #ea580c;
      margin-bottom: var(--space-2);
    }

    .renewal-card__opt-out svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    .renewal-card__assignee {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      font-size: var(--text-sm);
      color: var(--text-secondary);
    }

    .renewal-card__assignee svg {
      width: 16px;
      height: 16px;
    }

    .renewal-card__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: var(--space-3);
      border-top: 1px solid var(--border);
    }

    .renewal-card__notes-count {
      display: flex;
      align-items: center;
      gap: var(--space-1);
      font-size: var(--text-sm);
      color: var(--text-secondary);
    }

    .renewal-card__notes-count svg {
      width: 16px;
      height: 16px;
    }

    .renewal-card__actions {
      display: flex;
      gap: var(--space-2);
    }

    /* Compact variant */
    .renewal-card--compact {
      padding: var(--space-3);
    }

    .renewal-card--compact .renewal-card__info-grid {
      grid-template-columns: repeat(4, 1fr);
    }

    @media (max-width: 768px) {
      .renewal-card__header {
        flex-direction: column;
      }

      .renewal-card__info-grid {
        grid-template-columns: 1fr;
      }

      .renewal-card--compact .renewal-card__info-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  `;

  document.head.appendChild(styles);
}

export default { createRenewalCard, injectRenewalCardStyles };
